import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { useApolloClient } from 'react-apollo';
import classNames from 'classnames';

import MultiTargetSelection from '../../MultiTargetSelection';
import Alert from '../../../../../../components/global/Alert';
import { getErrorString } from '../../../../../../util/normalize-errors';
import {
  AlbumFolderForSelection,
  RootAlbumFoldersForSelection,
} from '../../../../../../graphql/queries/album-folder';
import uuidv4 from '../../../../../../util/uuid';
import useBasename from '../../../../../../components/routing/useBasename';
import { getBasenameAlbumFilter } from '../../../../../../util/role-definitions';
import Spinner from '../../../../../../components/global/Spinner';

const propTypes = {
  albumFolderTree: PropTypes.object,
  className: PropTypes.string,
  selectedTargets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  onSelectTarget: PropTypes.func,
  onDeselectTarget: PropTypes.func,
  computeNestedAlbumsTotal: PropTypes.bool,
  albumFolderSelectionEnabled: PropTypes.bool,
  photoGroupSelectionEnabled: PropTypes.bool,
  disabledTargets: PropTypes.arrayOf(PropTypes.string),
  onAlbumFolderOpen: PropTypes.func,
  rootAlbumFolderId: PropTypes.string,
};

const ProMultiTargetSelection = ({ rootAlbumFolderId, ...props }) => {
  const [rootAlbumFolder, setRootAlbumFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const client = useApolloClient();
  const basename = useBasename();

  const fetchAlbumFolder = async (albumFolderId = rootAlbumFolderId) => {
    setLoading(true);

    try {
      const {
        data: { albumFolder },
      } = await client.query({
        query: AlbumFolderForSelection,
        variables: {
          id: albumFolderId,
        },
      });

      return albumFolder;
    } catch (err) {
      setError(getErrorString(err));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchRootAlbumFolder = async () => {
    if (rootAlbumFolderId) {
      const rootAlbumFolderRecord = await fetchAlbumFolder(rootAlbumFolderId);
      if (rootAlbumFolderRecord) {
        setRootAlbumFolder(rootAlbumFolderRecord);
      }
    } else {
      // Fetch root album folders:
      const {
        data: { me: user },
      } = await client.query({
        query: RootAlbumFoldersForSelection,
      });

      const albumFolderFilter = getBasenameAlbumFilter(basename);
      const filteredAlbumFolders = user.album_folders.records.filter(
        albumFolderFilter,
      );

      // Use a pseudo-root album folder to represent all user galleries:
      const pseudoRootAlbumFolder = {
        id: uuidv4(),
        name: 'My Galleries',
        children: {
          albums: {
            records: [],
            total: 0,
          },
          album_folders: {
            records: filteredAlbumFolders,
            total: filteredAlbumFolders.length,
          },
        },
      };

      setRootAlbumFolder(pseudoRootAlbumFolder);
    }
  };

  useEffect(() => {
    fetchRootAlbumFolder();
  }, [rootAlbumFolderId]);

  const classes = classNames({
    'multi-target-disabled': loading,
  });

  return (
    <div className={classes}>
      {loading && (
        <div className="multi-target-spinner">
          <Spinner className="m-auto" />
        </div>
      )}
      {error && <Alert type="danger">{error}</Alert>}
      {rootAlbumFolder && (
        <MultiTargetSelection
          albumFolderTree={rootAlbumFolder}
          onAlbumFolderOpen={async ({
            setAlbumFolderTreePath,
            albumFolderTreePath,
            selectedAlbumFolder,
          }) => {
            const completeSelectedAlbumFolderRecord = await fetchAlbumFolder(
              selectedAlbumFolder.id,
            );

            if (!completeSelectedAlbumFolderRecord) {
              // There was an error:
              return;
            }

            setAlbumFolderTreePath([
              ...albumFolderTreePath,
              completeSelectedAlbumFolderRecord,
            ]);
          }}
          {...props}
        />
      )}
    </div>
  );
};

ProMultiTargetSelection.propTypes = propTypes;

export { ProMultiTargetSelection as ProMultiTargetSelectionComponent };

export default compose()(ProMultiTargetSelection);
